import {
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { type PioneerPointStatusWidgetProps } from "./types";

/**
 * Ref handle exposed by LegionPioneerPointStatus for imperative control.
 */
export interface LegionPioneerPointStatusHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildPioneerPointStatusWidgetUrl(
  props: PioneerPointStatusWidgetProps
): string {
  const { baseUrl, theme = "light" } = props;

  const url = new URL("/widget/pioneer-point-status", baseUrl);
  url.searchParams.set("theme", theme);

  return url.toString();
}

/**
 * LegionPioneerPointStatus – a React component that embeds the Legion
 * Pioneer Point Status widget via an iframe.
 *
 * @example
 * ```tsx
 * import { LegionPioneerPointStatus } from '@legionhandtech/lht-react-widgets';
 *
 * function App() {
 *   return (
 *     <LegionPioneerPointStatus
 *       baseUrl="https://your-legion-domain.com"
 *       authToken="your-jwt-token"
 *       theme="light"
 *     />
 *   );
 * }
 * ```
 */
export const LegionPioneerPointStatus = forwardRef<
  LegionPioneerPointStatusHandle,
  PioneerPointStatusWidgetProps
>(function LegionPioneerPointStatus(props, ref) {
  const {
    theme = "light",
    authToken,
    width = "100%",
    height = "180px",
    className,
    style,
    onLoad,
    onError,
    baseUrl,
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow) {
          iframe.contentWindow.location.reload();
        }
      },
    }),
    []
  );

  const src = useMemo(
    () => buildPioneerPointStatusWidgetUrl(props),
    [baseUrl, theme]
  );

  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (authToken && iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        { type: "AUTH_TOKEN", token: authToken },
        baseUrl
      );
    }
    onLoad?.({ widget: "pioneer-point-status", theme });
  }, [onLoad, theme, authToken, baseUrl]);

  const handleError = useCallback(() => {
    onError?.("Failed to load pioneer point status widget");
  }, [onError]);

  return (
    <div
      className={className}
      style={{ width, height, ...style }}
      data-testid="legion-pioneer-point-status"
    >
      <iframe
        ref={iframeRef}
        src={src}
        title="Pioneer Point Status Widget"
        width="100%"
        height="100%"
        frameBorder="0"
        scrolling="no"
        onLoad={handleLoad}
        onError={handleError}
        style={{ border: "none" }}
      />
    </div>
  );
});
